import * as THREE from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {OrbitControls} from 'three/addons/controls/OrbitControls.js';
import manifest from '../data/terrarium/manifest.json' with {type: 'json'};
import {createTerrariumScene} from './terrarium-scene/asset-comparison/scene-four-mass-family-ab.mjs';
import {PREVIEW_LABEL, CAMERA_NAMES, CAMERA_NEAR_METRES, CAMERA_FAR_METRES, cameraLimits, responsiveFov, loadVerifiedInputs, disposeSceneResources} from './terrarium-scene/runtime-contract.js';

const $ = selector => document.querySelector(selector);
const stage = $('#terrarium-stage'), host = $('#interactive-host'), fallback = $('#preview-fallback');
const status = $('#terrarium-status'), caption = $('#preview-caption'), help = $('#interaction-help');
const retry = $('#retry-preview'), interactiveButtons = [...document.querySelectorAll('[data-interactive]')];
const lifetime = new AbortController();
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
let active = null, stopped = false, generation = 0, state = 'loading', selectedView = 'front', readyPromise;

function setButtons(ready) { interactiveButtons.forEach(button => { button.disabled = !ready; }); }
function markView(name) { document.querySelectorAll('[data-camera]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.camera === name))); }
function cleanup(attempt) {
  if (!attempt || attempt.disposed) return;
  attempt.disposed = true;
  attempt.controller.abort();
  clearTimeout(attempt.timeout);
  cancelAnimationFrame(attempt.frame);
  attempt.observer?.disconnect();
  attempt.controls?.dispose();
  disposeSceneResources([attempt.world?.scene, ...attempt.assets.map(asset => asset.scene)]);
  attempt.world?.dispose?.();
  attempt.renderer?.renderLists.dispose();
  attempt.renderer?.dispose();
  attempt.renderer?.forceContextLoss();
  attempt.renderer?.domElement.remove();
  if (active === attempt) active = null;
}
function showFallback(message = 'The interactive scene could not start in this browser.') {
  state = 'fallback';
  setButtons(false);
  host.hidden = true;
  fallback.hidden = false;
  stage.setAttribute('aria-busy', 'false');
  caption.textContent = PREVIEW_LABEL;
  status.textContent = `${PREVIEW_LABEL}. ${message}`;
  help.textContent = 'Saved front view of the selected scene. Oblique, channel, orbit and zoom need interactive 3D; no matching saved oblique view is included.';
  retry.hidden = false;
  selectedView = 'front';
  markView('front');
}
function render(attempt) {
  if (stopped || attempt.disposed || active !== attempt || !attempt.world) return;
  try {
    attempt.controls.update();
    attempt.world.update(2 + (reducedMotion ? 0 : attempt.elapsed));
    attempt.renderer.render(attempt.world.scene, attempt.camera);
  } catch (error) {
    console.warn('Terrarium rendering stopped.', error);
    cleanup(attempt);
    if (!stopped) showFallback('The 3D view stopped. You can retry or return to the original landscape.');
  }
}
function resize(attempt) {
  if (!attempt.camera || attempt.disposed) return;
  const width = Math.max(1, stage.clientWidth), height = Math.max(1, stage.clientHeight);
  attempt.camera.aspect = width / height;
  attempt.camera.fov = responsiveFov(attempt.baseFov, width / height);
  attempt.camera.updateProjectionMatrix();
  attempt.renderer.setSize(width, height, false);
  render(attempt);
}
function selectView(name, announce = true) {
  if (!CAMERA_NAMES.includes(name)) return;
  if (state !== 'ready' || !active?.world) {
    if (name === 'front') { fallback.hidden = false; markView('front'); }
    return;
  }
  const attempt = active, source = attempt.world.cameras[name];
  if (!source?.isPerspectiveCamera || !Array.isArray(source.userData.target)) throw new Error('Reviewed camera is missing');
  attempt.camera.copy(source);
  attempt.camera.near = CAMERA_NEAR_METRES;
  attempt.camera.far = CAMERA_FAR_METRES;
  attempt.baseFov = source.fov;
  attempt.controls.target.fromArray(source.userData.target);
  Object.assign(attempt.controls, cameraLimits(name));
  attempt.controls.update();
  selectedView = name;
  markView(name);
  resize(attempt);
  if (announce) status.textContent = `${name.charAt(0).toUpperCase() + name.slice(1)} view. Drag to orbit, scroll or pinch to zoom. Camera movement stays outside the enclosure.`;
}
function moveCamera({azimuth = 0, polar = 0, zoom = 1}) {
  if (state !== 'ready' || !active) return;
  const attempt = active, limits = cameraLimits(selectedView);
  const spherical = new THREE.Spherical().setFromVector3(attempt.camera.position.clone().sub(attempt.controls.target));
  spherical.theta = THREE.MathUtils.clamp(spherical.theta + azimuth, limits.minAzimuthAngle, limits.maxAzimuthAngle);
  spherical.phi = THREE.MathUtils.clamp(spherical.phi + polar, limits.minPolarAngle, limits.maxPolarAngle);
  spherical.radius = THREE.MathUtils.clamp(spherical.radius * zoom, limits.minDistance, limits.maxDistance);
  attempt.camera.position.copy(attempt.controls.target).add(new THREE.Vector3().setFromSpherical(spherical));
  attempt.controls.update();
  render(attempt);
}

async function start() {
  if (stopped) return;
  cleanup(active);
  const serial = ++generation;
  const attempt = {controller: new AbortController(), assets: [], elapsed: 0, frame: 0, last: performance.now(), disposed: false};
  active = attempt;
  state = 'loading';
  setButtons(false);
  retry.hidden = true;
  host.hidden = true;
  fallback.hidden = false;
  stage.setAttribute('aria-busy', 'true');
  caption.textContent = 'Saved front preview · loading interactive 3D';
  status.textContent = 'Loading verified scene assets. The saved front preview remains available.';
  help.textContent = 'About 23 MB of local models and textures. View controls become available when all checks pass.';
  attempt.timeout = setTimeout(() => attempt.controller.abort(new Error('Scene loading timed out')), 90000);
  try {
    attempt.renderer = new THREE.WebGLRenderer({antialias: true, alpha: false, powerPreference: 'high-performance'});
    attempt.renderer.outputColorSpace = THREE.SRGBColorSpace;
    attempt.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    attempt.renderer.toneMappingExposure = 1;
    attempt.renderer.shadowMap.enabled = true;
    attempt.renderer.shadowMap.type = THREE.PCFShadowMap;
    attempt.renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.5));
    const canvas = attempt.renderer.domElement;
    canvas.tabIndex = 0;
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'Interactive terrarium environment. Arrow keys orbit, plus and minus zoom. R resets to the front view.');
    canvas.addEventListener('webglcontextlost', event => {
      event.preventDefault();
      if (active !== attempt || attempt.disposed) return;
      cleanup(attempt);
      if (!stopped) showFallback('The graphics context was lost. The saved scene image is still available.');
    }, {signal: attempt.controller.signal});
    canvas.addEventListener('keydown', event => {
      const actions = {ArrowLeft: {azimuth: -0.1}, ArrowRight: {azimuth: 0.1}, ArrowUp: {polar: -0.08}, ArrowDown: {polar: 0.08}, '+': {zoom: 0.9}, '=': {zoom: 0.9}, '-': {zoom: 1.1}};
      if (actions[event.key]) { event.preventDefault(); moveCamera(actions[event.key]); }
      else if (event.key.toLowerCase() === 'r') { event.preventDefault(); selectView('front'); }
    }, {signal: attempt.controller.signal});
    const manager = new THREE.LoadingManager();
    manager.setURLModifier(url => {
      if (!url.startsWith('blob:') && !url.startsWith('data:')) throw new Error('External model resources are disabled');
      return url;
    });
    const loader = new GLTFLoader(manager);
    const inputs = await loadVerifiedInputs(manifest, {
      signal: attempt.controller.signal,
      parseGLB: bytes => loader.parseAsync(bytes, ''),
      onAsset: asset => {
        if (attempt.disposed) disposeSceneResources([asset.scene]);
        else attempt.assets.push(asset);
      },
      onProgress: (completed, total) => {
        if (active === attempt) status.textContent = `Loading verified environment: ${completed} of ${total} files ready.`;
      },
    });
    attempt.controller.signal.throwIfAborted();
    if (stopped || generation !== serial || active !== attempt) { cleanup(attempt); return; }
    attempt.world = createTerrariumScene(THREE, inputs);
    if (!attempt.world.scene?.isScene || attempt.world.metadata.animalCount !== 0) throw new Error('Invalid environment-only scene');
    attempt.camera = new THREE.PerspectiveCamera();
    attempt.controls = new OrbitControls(attempt.camera, canvas);
    attempt.controls.enablePan = false;
    attempt.controls.enableDamping = false;
    attempt.controls.rotateSpeed = 0.55;
    attempt.controls.zoomSpeed = 0.65;
    attempt.controls.addEventListener('change', () => { if (reducedMotion) render(attempt); });
    attempt.renderer.debug.onShaderError = () => { throw new Error('Scene shader could not compile'); };
    clearTimeout(attempt.timeout);
    host.replaceChildren(canvas);
    host.hidden = false;
    fallback.hidden = true;
    state = 'ready';
    stage.setAttribute('aria-busy', 'false');
    setButtons(true);
    help.textContent = 'Drag to orbit · scroll or pinch to zoom · keyboard: arrows, + / −, R. Water is a visual approximation, not a hydraulic simulation.';
    selectView('front');
    if (attempt.disposed) return;
    attempt.observer = new ResizeObserver(() => resize(attempt));
    attempt.observer.observe(stage);
    attempt.last = performance.now();
    const frame = now => {
      if (stopped || attempt.disposed || active !== attempt) return;
      const delta = Math.min((now - attempt.last) / 1000, 0.05);
      attempt.last = now;
      if (!document.hidden) { attempt.elapsed += delta; render(attempt); }
      if (!attempt.disposed) attempt.frame = requestAnimationFrame(frame);
    };
    if (!reducedMotion) attempt.frame = requestAnimationFrame(frame);
  } catch (error) {
    const current = generation === serial && !stopped;
    cleanup(attempt);
    if (current) {
      console.warn('Terrarium preview is using its saved image.', error);
      showFallback(error?.name === 'AbortError' || /timed out/.test(String(error)) ? 'Loading did not finish. You can retry the 3D view.' : 'The 3D scene could not load or verify. The saved front view is available.');
    }
  }
}

for (const button of document.querySelectorAll('[data-camera]')) button.addEventListener('click', () => selectView(button.dataset.camera), {signal: lifetime.signal});
$('#orbit-left').addEventListener('click', () => moveCamera({azimuth: -0.12}), {signal: lifetime.signal});
$('#orbit-right').addEventListener('click', () => moveCamera({azimuth: 0.12}), {signal: lifetime.signal});
$('#zoom-in').addEventListener('click', () => moveCamera({zoom: 0.9}), {signal: lifetime.signal});
$('#zoom-out').addEventListener('click', () => moveCamera({zoom: 1.1}), {signal: lifetime.signal});
$('#reset-view').addEventListener('click', () => selectView('front'), {signal: lifetime.signal});
retry.addEventListener('click', () => { readyPromise = start(); }, {signal: lifetime.signal});
window.addEventListener('pagehide', () => { stopped = true; ++generation; lifetime.abort(); cleanup(active); }, {once: true});
window.addEventListener('pageshow', event => { if (event.persisted && stopped) location.reload(); });
window.terrariumPreview = {
  get ready() { return readyPromise; },
  getState: () => ({state, view: selectedView, animalCount: 0, previewOnly: true, camera: active?.camera ? {near: active.camera.near, far: active.camera.far, position: active.camera.position.toArray(), target: active.controls.target.toArray(), distance: active.camera.position.distanceTo(active.controls.target)} : null}),
};
readyPromise = start();
